import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import {
  AutocompleteInput,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  DateInput,
  Edit,
  FunctionField,
  List,
  NumberInput,
  ReferenceInput,
  SearchInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  useGetList,
  usePermissions,
  useRecordContext,
  type CreateProps,
  type EditProps,
  type ListProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { DisplayDateField } from "@/components/DisplayDateField";
import { hasPermission } from "@/utils/permission-utils";
import { MADRASA_ENROLLMENTS_CREATE } from "../shared/madrasa-permissions";
import { RecordTitle } from "../shared/RecordTitle";
import { EnrollmentFeesSection } from "./EnrollmentFeesSection";
import { WithdrawButton } from "./WithdrawButton";
import { toEnrollmentCreatePayload } from "./enrollment-utils";

type EnrollmentRecord = { status?: string };

const STATUS_CHOICES = [
  { id: "ACTIVE", name: "Active" },
  { id: "WITHDRAWN", name: "Withdrawn" },
];

const listFilters = [
  <SearchInput key="q" source="q" placeholder="Student name, ITS, admission no" alwaysOn />,
  <ReferenceInput key="academicYearId" source="academicYearId" reference="academicYears" alwaysOn>
    <SelectInput optionText="name" label="Academic year" emptyText="All years" />
  </ReferenceInput>,
  <ReferenceInput key="classId" source="classId" reference="madrasaClasses" alwaysOn>
    <SelectInput optionText="name" label="Class" emptyText="All classes" />
  </ReferenceInput>,
  <TextInput key="section" source="section" label="Section" alwaysOn />,
  <SelectInput key="status" source="status" label="Status" choices={STATUS_CHOICES} alwaysOn />,
];

const ListActions = () => {
  const { permissions } = usePermissions();
  if (!hasPermission(permissions, MADRASA_ENROLLMENTS_CREATE)) return <TopToolbar />;
  return (
    <TopToolbar>
      <Button component={Link} to="/madrasaPromote" size="small">
        Promote students
      </Button>
      <CreateButton />
    </TopToolbar>
  );
};

function StatusChip() {
  return (
    <FunctionField
      label="Status"
      render={(record: EnrollmentRecord) =>
        record.status === "ACTIVE" ? (
          <Chip size="small" color="success" label="Active" />
        ) : (
          <Chip size="small" label="Withdrawn" />
        )
      }
    />
  );
}

function EnrollmentsListInner({ currentYearId, ...props }: ListProps & { currentYearId?: string }) {
  return (
    <List
      {...props}
      sort={{ field: "className", order: "ASC" }}
      perPage={50}
      filters={listFilters}
      filterDefaultValues={{
        ...(currentYearId ? { academicYearId: currentYearId } : {}),
        status: "ACTIVE",
      }}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No enrollments yet"
          subtitle="Enroll students into a class for an academic year, or promote a whole class from the previous year."
          action={<CreateButton variant="contained" />}
        />
      }
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="admissionNo" label="Adm. no" />
        <TextField source="studentName" label="Student" />
        <TextField source="className" label="Class" />
        <TextField source="section" label="Section" emptyText="—" />
        <TextField source="academicYearName" label="Year" />
        <TextField source="rollNo" label="Roll no" emptyText="—" />
        <DisplayDateField source="enrolledOn" label="Enrolled" />
        <DisplayDateField source="leftOn" label="Left" />
        <StatusChip />
        <WithdrawButton />
      </Datagrid>
    </List>
  );
}

/** Opens on the current academic year's active enrollments, which is what people look for first. */
export function MadrasaEnrollmentsList(props: ListProps) {
  const { data, isPending } = useGetList("academicYears", {
    pagination: { page: 1, perPage: 1 },
    filter: { isCurrent: true },
  });
  if (isPending) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  return <EnrollmentsListInner {...props} currentYearId={data?.[0]?.id as string | undefined} />;
}

const ADMISSION_MODE_CHOICES = [
  { id: "AUTO", name: "Automatic (new only for a student's first enrollment)" },
  { id: "NEW", name: "New admission (admission fee applies)" },
  { id: "RETURNING", name: "Returning student (no admission fee)" },
];

export function MadrasaEnrollmentsCreate(props: CreateProps) {
  return (
    <Create
      {...props}
      redirect="list"
      transform={toEnrollmentCreatePayload}
      title="Enroll a student"
    >
      <SimpleForm
        warnWhenUnsavedChanges
        sx={{ maxWidth: 520 }}
        defaultValues={{ admissionMode: "AUTO" }}
      >
        <ReferenceInput
          source="studentId"
          reference="madrasaStudents"
          filter={{ status: "ACTIVE" }}
        >
          <AutocompleteInput
            label="Student"
            optionText={(s: { name?: string; admissionNo?: number }) =>
              s?.name ? `${s.name} (adm. ${s.admissionNo})` : ""
            }
            filterToQuery={(q: string) => ({ q })}
            isRequired
            fullWidth
          />
        </ReferenceInput>
        <ReferenceInput
          source="academicYearId"
          reference="academicYears"
          filter={{ isClosed: false }}
        >
          <SelectInput optionText="name" label="Academic year" isRequired fullWidth />
        </ReferenceInput>
        <ReferenceInput source="classId" reference="madrasaClasses" filter={{ isActive: true }}>
          <SelectInput optionText="name" label="Class" isRequired fullWidth />
        </ReferenceInput>
        <TextInput source="section" label="Section (e.g. A, B)" fullWidth />
        <NumberInput source="rollNo" label="Roll no (optional)" min={1} fullWidth />
        <DateInput
          source="enrolledOn"
          label="Enrolled on"
          fullWidth
          helperText="Fees start from this month. Leave empty for today (kept inside the academic year)."
        />
        <SelectInput
          source="admissionMode"
          label="Admission type"
          choices={ADMISSION_MODE_CHOICES}
          fullWidth
          isRequired
        />
      </SimpleForm>
    </Create>
  );
}

function EditNotice() {
  const record = useRecordContext<EnrollmentRecord>();
  if (!record || record.status === "ACTIVE") return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      This enrollment is withdrawn, so it can no longer be edited.
    </Alert>
  );
}

function EditFields() {
  const record = useRecordContext<EnrollmentRecord>();
  const locked = record?.status !== "ACTIVE";
  return (
    <>
      <EditNotice />
      <TextField source="studentName" label="Student" />
      <TextField source="className" label="Class" />
      <TextInput source="section" label="Section" disabled={locked} fullWidth />
      <TextField source="academicYearName" label="Academic year" />
      <NumberInput source="rollNo" label="Roll no" min={1} disabled={locked} fullWidth />
      <DateInput source="enrolledOn" label="Enrolled on" disabled={locked} fullWidth />
      <BooleanInput
        source="isNewAdmission"
        label="New admission (admission fee applies)"
        disabled={locked}
      />
      {record?.status === "WITHDRAWN" && (
        <DateInput source="leftOn" label="Left on" disabled fullWidth />
      )}
    </>
  );
}

const EditActions = () => (
  <TopToolbar>
    <WithdrawButton size="medium" />
  </TopToolbar>
);

export function MadrasaEnrollmentsEdit(props: EditProps) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      actions={<EditActions />}
      title={
        <RecordTitle
          prefix="Enrollment"
          sources={["studentName", "className", "academicYearName"]}
        />
      }
    >
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 520 }}>
        <EditFields />
      </SimpleForm>
      <EnrollmentFeesSection />
    </Edit>
  );
}

export default {
  list: MadrasaEnrollmentsList,
  create: MadrasaEnrollmentsCreate,
  edit: MadrasaEnrollmentsEdit,
  icon: AssignmentIndIcon,
  label: "Enrollments",
  options: { label: "Enrollments" },
  name: "madrasaEnrollments",
};
